/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, Input, OnChanges, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlfrescoApiService, NodePermissionDialogService, NodePermissionService, NodesApiService } from '@alfresco/adf-content-services';
import { NotificationService } from '@alfresco/adf-core';
import { GroupsApi, Node, PeopleApi, PermissionElement } from '@alfresco/js-api';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/** One line of the permissions table. */
interface PermissionRow {
  authorityId: string;
  displayName: string;
  isGroup: boolean;
  /** The Alfresco role name, e.g. `Coordinator`. */
  role: string;
  inherited: boolean;
  /** Inherited rows, and the site manager's own, cannot be changed here. */
  readonly: boolean;
  accessStatus: string;
  initial: string;
  tone: string;
  /**
   * When the right last could have changed. Alfresco stamps no time on an ACL
   * entry, so this is the last change of whatever carries it: the document for
   * a right set on it directly, the parent folder for an inherited one.
   */
  updatedAt: string;
}

type PermissionFilter = 'all' | 'inherited' | 'direct';

/** The Thai reading of each Alfresco role, shown under its own name. */
const ROLE_LABELS: Record<string, string> = {
  Coordinator: 'DMS.PERMISSIONS.ROLE_COORDINATOR',
  Collaborator: 'DMS.PERMISSIONS.ROLE_COLLABORATOR',
  Contributor: 'DMS.PERMISSIONS.ROLE_CONTRIBUTOR',
  Editor: 'DMS.PERMISSIONS.ROLE_EDITOR',
  Consumer: 'DMS.PERMISSIONS.ROLE_CONSUMER',
  SiteManager: 'DMS.PERMISSIONS.ROLE_SITE_MANAGER',
  SiteCollaborator: 'DMS.PERMISSIONS.ROLE_SITE_COLLABORATOR',
  SiteContributor: 'DMS.PERMISSIONS.ROLE_SITE_CONTRIBUTOR',
  SiteConsumer: 'DMS.PERMISSIONS.ROLE_SITE_CONSUMER'
};

const TONES = ['green', 'purple', 'blue', 'amber', 'red'];

/**
 * Access rights for one document (UI-19).
 *
 * The reference draws two cards: inheritance and its summary above, then the
 * table of who may do what. Reading and writing is left to ADF's permission
 * services — they own the ACL, the roles and the add-permission dialog — while
 * everything a person sees is laid out here, because ADF's own list has neither
 * the columns nor the shape the reference asks for.
 */
@Component({
  selector: 'app-document-permissions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatMenuModule,
    MatProgressBarModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './document-permissions.component.html',
  styleUrl: './document-permissions.component.scss'
})
export class DocumentPermissionsComponent implements OnChanges {
  @Input({ required: true }) nodeId!: string;

  private readonly nodes = inject(NodesApiService);
  private readonly permissions = inject(NodePermissionService);
  private readonly permissionDialog = inject(NodePermissionDialogService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly api = inject(AlfrescoApiService);

  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly node = signal<Node | null>(null);
  readonly rows = signal<PermissionRow[]>([]);
  readonly roles = signal<string[]>([]);
  readonly canEdit = signal(false);
  /** The row whose role is open for changing, if any. */
  readonly editing = signal<string | null>(null);

  readonly filter = signal<PermissionFilter>('all');
  readonly pageSize = signal(10);
  readonly page = signal(1);

  readonly filters: { id: PermissionFilter; label: string }[] = [
    { id: 'all', label: 'DMS.PERMISSIONS.FILTER_ALL' },
    { id: 'inherited', label: 'DMS.PERMISSIONS.TYPE_INHERITED' },
    { id: 'direct', label: 'DMS.PERMISSIONS.TYPE_DIRECT' }
  ];

  readonly pageSizes = [10, 25, 50];

  readonly inheritEnabled = computed(() => this.node()?.permissions?.isInheritanceEnabled ?? false);
  readonly inheritedCount = computed(() => this.rows().filter((row) => row.inherited).length);

  readonly visible = computed(() => {
    const filter = this.filter();
    return this.rows().filter((row) => filter === 'all' || (filter === 'inherited') === row.inherited);
  });

  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.visible().length / this.pageSize())));

  readonly paged = computed(() => {
    const start = (this.page() - 1) * this.pageSize();
    return this.visible().slice(start, start + this.pageSize());
  });

  readonly rangeFrom = computed(() => (this.visible().length ? (this.page() - 1) * this.pageSize() + 1 : 0));
  readonly rangeTo = computed(() => Math.min(this.page() * this.pageSize(), this.visible().length));

  /** Display names already looked up, so a repeated authority costs nothing. */
  private readonly names = new Map<string, string>();

  ngOnChanges(): void {
    if (this.nodeId) {
      this.load();
    }
  }

  load(): void {
    this.loading.set(true);
    this.nodes
      .getNode(this.nodeId, { include: ['permissions', 'path', 'allowableOperations'] })
      .pipe(
        switchMap((node) =>
          forkJoin({
            node: of(node),
            roles: this.permissions.getNodeRoles(node).pipe(catchError(() => of(node.permissions?.settable ?? []))),
            // an inherited right is dated by the folder it comes through
            parent: node.parentId ? this.nodes.getNode(node.parentId).pipe(catchError(() => of(null))) : of(null)
          })
        )
      )
      .subscribe({
        next: ({ node, roles, parent }) => this.apply(node, roles, parent),
        error: () => {
          this.loading.set(false);
          this.notification.showError(this.translate.instant('DMS.PERMISSIONS.LOAD_FAIL'));
        }
      });
  }

  private apply(node: Node, roles: string[], parent: Node | null): void {
    this.node.set(node);
    this.roles.set(roles ?? []);
    this.canEdit.set((node.allowableOperations ?? []).includes('updatePermissions'));

    const manager = this.managerAuthority(node);
    const when = (value: Date | string | undefined): string => (value ? String(value) : '');
    const dates = { direct: when(node.modifiedAt), inherited: when(parent?.modifiedAt) };
    const build = (list: PermissionElement[] | undefined, inherited: boolean): PermissionRow[] =>
      (list ?? []).map((entry) => this.toRow(entry, inherited, manager, inherited ? dates.inherited : dates.direct));

    const rows = [...build(node.permissions?.inherited, true), ...build(node.permissions?.locallySet, false)].sort(
      (a, b) => Number(b.inherited) - Number(a.inherited) || a.displayName.localeCompare(b.displayName, 'th')
    );

    this.rows.set(rows);
    this.page.set(1);
    this.loading.set(false);
    this.resolveNames(rows);
  }

  /** The site manager keeps its own rights whatever anyone does here. */
  private managerAuthority(node: Node): string {
    const site = (node.path?.elements ?? []).find((element: any) => element.nodeType === 'st:site');
    return site ? `GROUP_site_${site.name}_SiteManager` : '';
  }

  private toRow(entry: PermissionElement, inherited: boolean, manager: string, updatedAt: string): PermissionRow {
    const authorityId = entry.authorityId ?? '';
    const isGroup = authorityId.startsWith('GROUP_');
    const displayName = this.names.get(authorityId) ?? this.plainName(authorityId);
    return {
      authorityId,
      displayName,
      isGroup,
      role: entry.name ?? '',
      inherited,
      readonly: inherited || authorityId === manager,
      accessStatus: entry.accessStatus ?? 'ALLOWED',
      initial: (displayName.trim()[0] ?? '?').toUpperCase(),
      tone: TONES[this.hash(authorityId) % TONES.length],
      updatedAt
    };
  }

  /** A readable name before the repository has been asked for the real one. */
  private plainName(authorityId: string): string {
    const bare = authorityId.replace(/^GROUP_/, '').replace(/_/g, ' ');
    return bare.charAt(0).toUpperCase() + bare.slice(1);
  }

  private hash(value: string): number {
    let total = 0;
    for (const char of value) {
      total = (total + char.charCodeAt(0)) % 9973;
    }
    return total;
  }

  /**
   * Asks the repository what each authority is really called. The table is
   * already on screen by then, so a slow directory only fills names in later
   * rather than holding the whole panel up.
   */
  private resolveNames(rows: PermissionRow[]): void {
    const people = new PeopleApi(this.api.getInstance() as any);
    const groups = new GroupsApi(this.api.getInstance() as any);

    for (const row of rows) {
      if (this.names.has(row.authorityId)) {
        continue;
      }
      const lookup = row.isGroup
        ? (groups.getGroup(row.authorityId) as Promise<any>).then((res) => res?.entry?.displayName)
        : (people.getPerson(row.authorityId) as Promise<any>).then((res) => res?.entry?.displayName);

      lookup
        .then((name: string) => {
          if (!name || name === row.authorityId) {
            return;
          }
          this.names.set(row.authorityId, name);
          this.rows.update((current) =>
            current.map((entry) =>
              entry.authorityId === row.authorityId ? { ...entry, displayName: name, initial: (name.trim()[0] ?? '?').toUpperCase() } : entry
            )
          );
        })
        .catch(() => undefined);
    }
  }

  // ── labels ─────────────────────────────────────────────────────────────────

  roleLabel(role: string): string {
    const key = ROLE_LABELS[role];
    return key ? this.translate.instant(key) : role;
  }

  filterLabel(): string {
    const current = this.filters.find((entry) => entry.id === this.filter());
    return current ? this.translate.instant(current.label) : '';
  }

  // ── acting on it ───────────────────────────────────────────────────────────

  setFilter(filter: PermissionFilter): void {
    this.filter.set(filter);
    this.page.set(1);
  }

  setPageSize(size: number): void {
    this.pageSize.set(size);
    this.page.set(1);
  }

  goToPage(page: number): void {
    this.page.set(Math.min(Math.max(1, page), this.pageCount()));
  }

  toggleInheritance(enabled: boolean): void {
    const node = this.node();
    if (!node || this.busy()) {
      return;
    }
    this.busy.set(true);
    this.nodes.updateNode(this.nodeId, { permissions: { isInheritanceEnabled: enabled } } as any, { include: ['permissions'] }).subscribe({
      next: () => {
        this.busy.set(false);
        this.notification.showInfo(this.translate.instant(enabled ? 'DMS.PERMISSIONS.INHERIT_ON_DONE' : 'DMS.PERMISSIONS.INHERIT_OFF_DONE'));
        this.load();
      },
      error: () => {
        this.busy.set(false);
        this.notification.showError(this.translate.instant('DMS.PERMISSIONS.INHERIT_FAIL'));
        this.load();
      }
    });
  }

  startEdit(row: PermissionRow): void {
    if (!row.readonly && this.canEdit()) {
      this.editing.set(row.authorityId);
    }
  }

  cancelEdit(): void {
    this.editing.set(null);
  }

  changeRole(row: PermissionRow, role: string): void {
    this.editing.set(null);
    const node = this.node();
    if (!node || !role || role === row.role) {
      return;
    }
    this.busy.set(true);
    this.permissions
      .updatePermissionRole(node, { authorityId: row.authorityId, name: role, accessStatus: row.accessStatus } as PermissionElement)
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.notification.showInfo(this.translate.instant('DMS.PERMISSIONS.SAVED'));
          this.load();
        },
        error: () => {
          this.busy.set(false);
          this.notification.showError(this.translate.instant('DMS.PERMISSIONS.SAVE_FAIL'));
          this.load();
        }
      });
  }

  remove(row: PermissionRow): void {
    const node = this.node();
    if (!node || row.readonly) {
      return;
    }
    if (!confirm(this.translate.instant('DMS.PERMISSIONS.DELETE_CONFIRM', { p0: row.displayName }))) {
      return;
    }
    // ADF edits the list in place, so it is handed a copy of its own
    const clone = { ...node, permissions: { ...node.permissions, locallySet: [...(node.permissions?.locallySet ?? [])] } } as Node;
    this.busy.set(true);
    this.permissions
      .removePermission(clone, { authorityId: row.authorityId, name: row.role, accessStatus: row.accessStatus } as PermissionElement)
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.notification.showInfo(this.translate.instant('DMS.PERMISSIONS.DELETED'));
          this.load();
        },
        error: () => {
          this.busy.set(false);
          this.notification.showError(this.translate.instant('DMS.PERMISSIONS.DELETE_FAIL'));
          this.load();
        }
      });
  }

  add(): void {
    this.permissionDialog.updateNodePermissionByDialog(this.nodeId, 'DMS.PERMISSIONS.ADD_TITLE').subscribe({
      next: () => {
        this.notification.showInfo(this.translate.instant('DMS.PERMISSIONS.ADDED'));
        this.load();
      },
      error: () => this.load()
    });
  }

  trackByAuthority(_index: number, row: PermissionRow): string {
    return row.authorityId + '|' + row.inherited;
  }
}
